import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { useLocation } from 'react-router-dom';

import { ChartOptions } from 'chart.js';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  ScatterController,
  TimeScale,
  TimeSeriesScale,
  Tooltip,
} from 'chart.js';
import CommonSelect from 'components/select/CustomSelect';
import { Typography } from 'enable-ui';

import { WarningNoDefault } from 'modules/dashboard/components/WarningNoDefault';

import { COLOR, COLOR_CONFIG } from 'constants/color';
import { PATH } from 'constants/path';

import { useAnalyticsController } from '../core/controller/analytics';
import { AnalyticsEnum } from '../core/enum';
import { AnalyticsChart } from '../style/analytics-chart';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  TimeScale,
  TimeSeriesScale,
  ScatterController,
);

const AnalyticsPage = () => {
  const location = useLocation();

  const {
    assessment,
    assessments,
    searchSelect,
    onChangeAssessment,
    onChangeSearchAssessment,
    analyticsData,
    onClickHideData,
    hideData,
    getTrendLineData,
  } = useAnalyticsController();

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const data = useMemo(() => {
    return {
      datasets: [
        {
          label: 'line',
          data: getTrendLineData(analyticsData?.normal || []),
          type: 'line',
          borderColor: '#383ECD',
          borderWidth: 4,
          fill: false,
          pointStyle: false,
          hidden: hideData.find((item) => item.key === AnalyticsEnum.normal)?.hide,
        },
        {
          type: 'line',
          data: getTrendLineData(analyticsData?.afterExercise || []),
          borderColor: '#52C41A',
          borderWidth: 4,
          fill: false,
          pointStyle: false,
          hidden: hideData.find((item) => item.key === AnalyticsEnum.afterExercise)?.hide,
        },
        {
          type: 'scatter',
          data: analyticsData?.records,
          backgroundColor: '#FD8A24',
          borderColor: '#FD8A24',
          borderWidth: 1,
          radius: 4,
          clip: false,
          hidden: hideData.find((item) => item.key === AnalyticsEnum.records)?.hide,
        },
      ],
    };
  }, [analyticsData, hideData]);

  const options: ChartOptions<'scatter'> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: `${assessment?.label} (${analyticsData?.records[0]?.method})`,
          font: {
            weight: 600,
            size: 16,
            lineHeight: '22px',
          },
        },
        min: 0,
      },
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Age',
          font: {
            weight: 600,
            size: 16,
            lineHeight: '22px',
          },
        },
        grid: {
          display: false,
        },
        min: 60,
        max: 90,
        ticks: {
          autoSkip: false,
        },
      },
    },
  };

  return (
    <div className="full-width">
      {assessments.length > 0 ? (
        <>
          {!isInstitutionDetail && (
            <Typography
              style={{
                fontSize: 26,
                lineHeight: '36px',
                fontWeight: 500,
                color: COLOR.surfaceNeutralBold['neutral-11'],
                marginBottom: 24,
              }}
            >
              Analytics
            </Typography>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 32,
            }}
          >
            <CommonSelect
              value={assessments.find((item: any) => item.value === assessment?.value)}
              options={assessments.filter((item: any) =>
                item.label.toLowerCase().includes(searchSelect.value.toLowerCase()),
              )}
              onChange={onChangeAssessment}
              onInputChange={onChangeSearchAssessment}
              controlStyle={{ width: 300 }}
            />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {[
                {
                  prefixStyle: {
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#FD8A24',
                  },
                  text: 'Lastest records',
                  key: AnalyticsEnum.records,
                },
                {
                  prefixStyle: {
                    width: 20,
                    height: 5,
                    borderRadius: 99,
                    backgroundColor: '#383ECD',
                  },
                  text: 'Normal mobility',
                  key: AnalyticsEnum.normal,
                },
                {
                  prefixStyle: {
                    width: 20,
                    height: 5,
                    borderRadius: 99,
                    backgroundColor: '#52C41A',
                  },
                  text: 'After-exercise mobility',
                  key: AnalyticsEnum.afterExercise,
                },
              ].map((item, index) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginLeft: index === 0 ? 0 : 24,
                    cursor: 'pointer',
                  }}
                  key={index}
                  onClick={() => onClickHideData(item.key)}
                >
                  <div style={item.prefixStyle}></div>
                  <Typography
                    style={{
                      fontSize: 18,
                      lineHeight: '26px',
                      color: hideData.find((el) => el.key === item.key)?.hide
                        ? COLOR_CONFIG['neutral-text']
                        : COLOR.surfaceNeutralBold['neutral-11'],
                    }}
                  >
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
          <div style={{ width: '100%', height: '500px' }}>
            <AnalyticsChart>
              <Chart
                type="scatter"
                data={data as any}
                options={options}
                className="analytics-chart"
              />
            </AnalyticsChart>
          </div>
        </>
      ) : (
        <WarningNoDefault />
      )}
    </div>
  );
};

export default AnalyticsPage;
